import React, { useState } from "react";
import { Mutation, Query } from "react-apollo";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import queries from "../../../queries";
import { agenda2Themes } from "@treeosk/common";
import FileUploader from "../../uploaders/FileUploader";
import ImageUploader from "../../uploaders/ImageUploader";
import PaletteColorPicker from "../../PaletteColorPicker";
import EditorSection from "./EditorSection";
import AgendaTableauDaysEditor from "./AgendaTableauDaysEditor";
import AgendaTableauRowsEditor from "./AgendaTableauRowsEditor";

// Editor of the "Agenda Tableau" cell (cell_type 'agenda_tableau'): day tabs above/below a table whose
// column count (2 to 5) and column titles are configurable, with rows managed by hand or via CSV import.

const COLOR_GROUPS = [
  {
    id: 'module',
    title: 'Module',
    fields: [
      ['background_color', 'Fond du module'],
      ['text_color', 'Couleur du texte'],
    ],
  },
  {
    id: 'day_filter',
    title: 'Filtre par jour',
    fields: [
      ['background_color_filter_day', 'Fond du filtre'],
      ['background_color_filter_day_checked', 'Fond du jour sélectionné'],
      ['text_color_day_filter', 'Texte des jours'],
      ['text_color_day_filter_selected', 'Texte du jour sélectionné'],
      ['border_color_selected_day', 'Bordure du jour sélectionné'],
    ],
  },
  {
    id: 'table',
    title: 'Tableau',
    fields: [
      ['background_color_header', "Fond de l'en-tête"],
      ['text_color_header', "Texte de l'en-tête"],
      ['background_color_row', 'Fond des lignes'],
      ['text_color_row', 'Texte des lignes'],
      ['text_color_first_column', 'Texte de la première colonne (en gras)'],
      ['border_color_table', 'Bordure du tableau'],
    ],
  },
];

const SIZE_FIELDS = [
  ['font_size_filter_day', 'Taille du filtre par jour (em)'],
  ['font_size_header', "Taille de l'en-tête (em)"],
  ['font_size_cell', 'Taille des lignes (em)'],
];

const NUMBER_FIELDS = [...SIZE_FIELDS.map(([key]) => key)];
const COLOR_KEYS = _.flatMap(COLOR_GROUPS, (group) => group.fields.map(([key]) => key));
const COLUMN_TITLE_KEYS = ['column_1_title', 'column_2_title', 'column_3_title', 'column_4_title', 'column_5_title'];
const ALL_KEYS = [
  ...COLOR_KEYS, ...NUMBER_FIELDS, 'bolder_radius_filter_day', 'days_position', 'columns_count', ...COLUMN_TITLE_KEYS,
  'color_theme', 'background_image_uri', 'background_fit', 'font_family',
];

const DAYS_POSITIONS = [
  ['above', 'Au-dessus du tableau'],
  ['below', 'Au-dessous du tableau'],
];

const BACKGROUND_FITS = [
  ['cover', 'Remplir la cell (l\'image peut être recadrée)'],
  ['contain', "Image entière (des bords peuvent rester vides)"],
  ['stretch', 'Étirer à la taille de la cell'],
];

const COLOR_THEMES = [
  ['custom', 'Personnalisé'],
  ['euroseeds', 'Euroseeds (couleurs et fond)'],
];

// Every color field offers the same three swatches: white, black and the Euroseeds green.
// Blanc, noir, vert et violet Euroseeds (le même violet que dans les thèmes de agenda2Themes.js)
const COLOR_PALETTE = ['#FFFFFF', '#000000', '#A6C82A', '#654455'];

const label = (key, defaultMessage) => (
  <FormattedMessage id={`agenda_tableau.edit.${key}`} defaultMessage={defaultMessage} />
);

const toRgba = ({ rgb }) => `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`;

function AgendaTableauCellEditor({ cell }) {
  if (!cell || !cell.id) {
    return null;
  }

  return (
    <Query query={queries.cells.agenda_tableau.get} variables={{ id: cell.id }}>
      {({ loading, error, data, refetch }) => {
        if (loading) return null;
        if (error) return `Error!: ${error}`;

        const { agendaTableauCell } = data;
        if (!agendaTableauCell) return null;

        return (
          <Mutation
            mutation={queries.cells.agenda_tableau.update}
            refetchQueries={() => [{ query: queries.cells.agenda_tableau.get, variables: { id: cell.id } }]}
          >
            {(updateCell) => (
              <AgendaTableauCellEditorForm
                cell={agendaTableauCell}
                updateCell={updateCell}
                refetch={refetch}
              />
            )}
          </Mutation>
        );
      }}
    </Query>
  );
}

function AgendaTableauCellEditorForm({ cell, updateCell, refetch }) {
  const [values, setValues] = useState(() => _.pick(cell, ALL_KEYS));
  const set = (key, value) => setValues((previous) => ({ ...previous, [key]: value }));
  // The Euroseeds theme fills in its colors (still editable afterwards) and turns its background image on.
  const setTheme = (theme) => setValues((previous) => ({
    ...previous,
    ...(theme === 'euroseeds' ? agenda2Themes.EUROSEEDS_COLORS_TABLEAU : {}),
    color_theme: theme,
  }));

  const save = () => {
    const input = { id: cell.id, ...values };
    NUMBER_FIELDS.forEach((key) => { input[key] = parseFloat(values[key]); });
    input.columns_count = parseInt(values.columns_count, 10);
    updateCell({ variables: { input } });
  };

  const columnsCount = Math.min(Math.max(parseInt(values.columns_count, 10) || 2, 2), 5);

  return (
    <div className="tsk-editor-form">
      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.import" defaultMessage="Import CSV" />}>
        <p style={{ margin: '0 0 8px' }}>
          Colonnes attendues : <code>day</code> (date, ex. 26/10/26), puis <code>column_1</code> à{' '}
          <code>column_{columnsCount}</code> selon le nombre de colonnes ci-dessous. L'import remplace tous les
          jours et toutes les lignes existants.
        </p>
        <div className="tsk-input-line tsk-input-line--full">
          <label><FormattedMessage id="plan.edit.label.csv" defaultMessage="Fichier CSV" /></label>
          <FileUploader cellId={cell.id} accept={'.csv'} uploadPath='/csv_agenda_tableau/' onUpload={() => refetch()} />
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.columns" defaultMessage="Colonnes" />} defaultOpen={false}>
        <div className="tsk-input-line">
          <label>{label('columns_count', 'Nombre de colonnes')}</label>
          <select value={columnsCount} onChange={(e) => set('columns_count', e.target.value)}>
            {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {COLUMN_TITLE_KEYS.slice(0, columnsCount).map((key, i) => (
          <div className="tsk-input-line" key={key}>
            <label>{label(key, `Titre de la colonne ${i + 1}`)}</label>
            <input type="text" value={values[key] || ''} onChange={(e) => set(key, e.target.value)} />
          </div>
        ))}
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.days" defaultMessage="Jours (ajouter, changer la date)" />} defaultOpen={false}>
        <AgendaTableauDaysEditor cell={cell} refetch={refetch} />
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.rows" defaultMessage="Lignes du tableau" />} defaultOpen={false}>
        <AgendaTableauRowsEditor cell={cell} refetch={refetch} />
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.options" defaultMessage="Affichage" />}>
        <div className="tsk-input-line">
          <label>{label('days_position', 'Position des jours')}</label>
          <select value={values.days_position || 'above'} onChange={(e) => set('days_position', e.target.value)}>
            {DAYS_POSITIONS.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
        <div className="tsk-input-line">
          <label>{label('font_family', 'Typographie du tableau')}</label>
          <input
            type="text"
            value={values.font_family || ''}
            placeholder="OpenSans-Regular, 'Open Sans', sans-serif"
            onChange={(e) => set('font_family', e.target.value)}
          />
        </div>
        <p style={{ margin: '0 0 8px', opacity: 0.7 }}>
          Open Sans par défaut. Pour changer, indique le nom d'une police du projet (Paramètres du projet &gt; Polices),
          par exemple <code>OpenSans-Light</code>, ou une police système comme <code>Arial</code>.
        </p>
        <div className="tsk-input-line">
          <label>{label('bolder_radius_filter_day', 'Filtre par jour arrondi')}</label>
          <input type="checkbox" checked={!!values.bolder_radius_filter_day} onChange={(e) => set('bolder_radius_filter_day', e.target.checked)} />
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.background" defaultMessage="Fond" />} defaultOpen={false}>
        <p style={{ margin: '0 0 8px' }}>
          Ajoute ta propre image de fond quand tu en as besoin. Elle remplace le fond du thème Euroseeds ; retire-la
          pour retrouver le fond du thème (ou aucun fond). La couleur de fond se règle dans Couleurs &gt; Module.
        </p>
        <div className="tsk-input-line tsk-input-line--full">
          <label>{label('background_image_uri', 'Image de fond')}</label>
          <ImageUploader
            value={values.background_image_uri}
            canDelete
            onUpload={(uri) => set('background_image_uri', uri)}
            onDelete={() => set('background_image_uri', null)}
          />
        </div>
        <div className="tsk-input-line">
          <label>{label('background_fit', "Ajustement de l'image")}</label>
          <select value={values.background_fit || 'cover'} onChange={(e) => set('background_fit', e.target.value)}>
            {BACKGROUND_FITS.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.colors" defaultMessage="Couleurs" />} defaultOpen={false}>
        <div className="tsk-input-line">
          <label>{label('color_theme', 'Thème de couleurs')}</label>
          <select value={values.color_theme || 'custom'} onChange={(e) => setTheme(e.target.value)}>
            {COLOR_THEMES.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
        {COLOR_GROUPS.map((group) => (
          <EditorSection nested key={group.id} title={label(`group.${group.id}`, group.title)}>
            {group.fields.map(([key, text]) => (
              <div className="tsk-input-line" key={key}>
                <label>{label(key, text)}</label>
                <PaletteColorPicker
                  palette={COLOR_PALETTE}
                  allowNone
                  disableAlpha={false}
                  color={values[key]}
                  onChange={(color) => set(key, toRgba(color))}
                />
              </div>
            ))}
          </EditorSection>
        ))}
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agenda_tableau.edit.section.sizes" defaultMessage="Tailles" />} defaultOpen={false}>
        {SIZE_FIELDS.map(([key, text]) => (
          <div className="tsk-input-line" key={key}>
            <label>{label(key, text)}</label>
            <input type="number" step="0.1" min="0.3" value={values[key]} onChange={(e) => set(key, e.target.value)} />
          </div>
        ))}
      </EditorSection>

      <button className="tsk-button" onClick={save}>
        <FormattedMessage id={'common.button.submit'} />
      </button>
    </div>
  );
}

export default AgendaTableauCellEditor;
